/**
 * useAdaptiveSignalsVM.ts — MER Algo Footprints v8.5.2
 *
 * Thin React shell over the canonical adaptive output layer.
 *
 * ── What this does ────────────────────────────────────────────────────────────
 *
 * 1. Reads engine outputs (via getSymbolContext + getThesis)
 * 2. Reads governance state (currentModifiers, mode)
 * 3. Calls buildAdaptiveOutputs() — THE canonical application layer
 * 4. Passes adapted conviction into buildSignalsVM()
 * 5. Calls recordAuditEntry() for each audit entry produced (de-duplicated)
 * 6. Returns adapted SignalsVM + adaptation metadata to consumer
 *
 * ── What this does NOT do ────────────────────────────────────────────────────
 *
 * ✗ Does NOT call applyConfidenceModifier() directly
 * ✗ Does NOT contain any modifier application logic
 * ✗ Does NOT duplicate the adaptation path
 *
 * All application logic lives in buildAdaptiveOutputs() (adaptiveOutput.ts).
 * This hook is purely a React integration wrapper.
 *
 * ── Governance control flow ───────────────────────────────────────────────────
 *
 *   governance.rollback() → currentModifiers changes
 *       → govSnap read fresh on next render
 *       → buildAdaptiveOutputs() sees new (neutral) modifier
 *       → returns passthrough
 *       → SignalsVM reflects reverted conviction immediately
 *
 *   governance.freeze() → mode = 'frozen'
 *       → buildAdaptiveOutputs() guard: govMode !== 'active' → passthrough
 *
 * ── Advisory mode safety ─────────────────────────────────────────────────────
 *
 * Any of the following → buildAdaptiveOutputs() returns base outputs unchanged:
 *   - REACT_APP_ENABLE_ACTIVE_ADAPTATION !== 'true'
 *   - govMode !== 'active'
 *   - no 'calib:global' in currentModifiers
 *   - modifier is neutral (≈ 1.0)
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useStore } from '../../store';
import { getSymbolContext } from '../../selectors/symbolContext';
import { getThesis } from '../../ai/thesisEngine';
import { buildSignalsVM, type SignalsVM } from './index';
import { getGovernanceSnapshot, recordAuditEntry } from '../../domains/learning/adaptiveGovernance';
import { buildAdaptiveOutputs } from '../../domains/learning/adaptiveOutput';
import type { RegimeKey } from '../../domains/learning/adaptiveLearning';

// ── Derive structured RegimeKey from live context ─────────────────────────────

function _buildRegimeKey(ctx: ReturnType<typeof getSymbolContext>): RegimeKey {
  return {
    volatility: 'unknown',
    gamma:      ctx?.gamma?.regime ?? 'unknown',
    thesis:     ctx?.regime?.dominant ?? 'unknown',
    trap:       (ctx?.trapInterpretation?.trapClassification &&
                 ctx.trapInterpretation.trapClassification !== 'none')
                  ? ctx.trapInterpretation.trapClassification
                  : null,
  };
}

// ── Result type ───────────────────────────────────────────────────────────────

export interface UseAdaptiveSignalsVMResult {
  vm:              SignalsVM;
  modifierApplied: boolean;
  appliedModifier: number | null;
  isAdvisoryMode:  boolean;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAdaptiveSignalsVM(symbol: string): UseAdaptiveSignalsVMResult {
  const { symbolData, settings, weights } = useStore();
  const ctx    = symbol ? getSymbolContext(symbol, symbolData[symbol], settings, weights) : null;
  const thesis = symbol ? getThesis(symbol) : null;

  // Re-read governance when symbol changes (governance changes propagate via govVersion)
  const [govVersion, setGovVersion] = useState(0);
  useEffect(() => { setGovVersion(v => v + 1); }, [symbol]);

  const govSnap  = useMemo(() => getGovernanceSnapshot(), [govVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const regimeKey = useMemo(() => _buildRegimeKey(ctx), [ctx]);

  // ── Canonical adaptive output — single call, no duplicate logic ────────────
  // Map GovernanceMode to AdaptiveMode — 'frozen' maps to 'advisory' (passthrough)
  const adaptiveMode = govSnap.mode === 'active' ? 'active' : 'advisory';

  const adaptiveResult = useMemo(() => buildAdaptiveOutputs(
    { conviction: thesis?.current?.conviction ?? null },
    govSnap.currentModifiers,
    adaptiveMode,
    regimeKey,
  ), [thesis?.current?.conviction, govSnap.currentModifiers, adaptiveMode, regimeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build SignalsVM — pass adapted conviction when active, otherwise null (advisory passthrough)
  const vm = useMemo(() => {
    if (!adaptiveResult.anyModified) {
      // Advisory passthrough — identical to unadapted baseline
      return buildSignalsVM(symbol, ctx, thesis, null);
    }
    // Active — override conviction via a synthetic calibProfile that produces no further scaling
    // We pre-computed the adapted conviction in buildAdaptiveOutputs; pass it directly
    // by building a neutral calibProfile that leaves conviction unchanged at the adapted value.
    // The cleanest approach: patch only the conviction field in a custom override.
    return buildSignalsVM(symbol, ctx, thesis, adaptiveResult.conviction);
  }, [symbol, ctx, thesis, adaptiveResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Centralized audit emission (de-duplicated) ────────────────────────────
  const lastEmittedModifier = useRef<number | null>(null);
  const [modifierApplied, setModifierApplied]   = useState(false);
  const [appliedModifier, setAppliedModifier]   = useState<number | null>(null);

  useEffect(() => {
    if (!adaptiveResult.anyModified || adaptiveResult.auditEntries.length === 0) {
      setModifierApplied(false);
      setAppliedModifier(null);
      return;
    }
    const entry  = adaptiveResult.auditEntries[0];
    const newMod = entry.modifier;
    if (lastEmittedModifier.current !== newMod) {
      recordAuditEntry(entry);
      lastEmittedModifier.current = newMod;
    }
    setModifierApplied(true);
    setAppliedModifier(newMod);
  }, [adaptiveResult]);

  return {
    vm,
    modifierApplied,
    appliedModifier,
    isAdvisoryMode: !adaptiveResult.anyModified,
  };
}
